import React, { useState, useRef } from 'react';
import {
  Modal,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Spinner,
  ProgressBar,
  Card,
  List,
  Thumbnail,
  Icon,
} from '@shopify/polaris';
import { UploadIcon, ExportIcon, AlertCircleIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { withApiErrorHandling } from '../utils/apiErrorHandler';

interface CallLogImportProps {
  open: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
  onJobStatusChange?: () => void;
  onNavigateToImportJobs?: () => void;
}

interface ImportJob {
  id: number;
  status: string;
  progress: number;
  total_records: number;
  processed_records: number;
  error_count: number;
  success_count: number;
  created_count: number;
  updated_count: number;
  created_at: string;
  duration_formatted: string;
  success_rate: number;
  error_rate: number;
}

const CallLogImport: React.FC<CallLogImportProps> = ({ open, onClose, onImportComplete, onJobStatusChange, onNavigateToImportJobs }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importJob, setImportJob] = useState<ImportJob | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please select a valid CSV, XLS, or XLSX file.');
        return;
      }

      // Validate file size (50MB max)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (selectedFile.size > maxSize) {
        setError('File size too large. Maximum allowed size is 50MB.');
        return;
      }

      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await axios.get('/import_jobs/sample_csv', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'call_logs_import_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample file:', error);
      setError('Failed to download sample file. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await withApiErrorHandling(
        () => axios.post('/import_jobs', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          },
        })
      ) as any;

      if (result && result.success) {
        setImportJob(result.data);
        setSuccess('File uploaded successfully! Import job has been started.');
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Start polling for job status
        startPolling(result.data.id);
      } else {
        // Handle error from withApiErrorHandling
        setError('Failed to upload file. Please try again.');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      // Extract error message from various possible sources
      let errorMessage = 'Failed to upload file. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const startPolling = (jobId: number) => {
    const interval = setInterval(async () => {
      try {
        const result = await withApiErrorHandling(
          () => axios.get(`/import_jobs/${jobId}`)
        ) as any;

        if (result && result.success) {
          const job = result.data;
          setImportJob(job);

          if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
            clearInterval(interval);
            setPollingInterval(null);
            
            if (job.status === 'completed') {
              setSuccess(`Import completed successfully! ${job.success_count} records imported, ${job.error_count} errors.`);
              if (onImportComplete) {
                onImportComplete();
              }
              if (onJobStatusChange) {
                onJobStatusChange();
              }
            } else if (job.status === 'failed') {
              setError(`Import failed: ${job.error_message || 'Unknown error occurred'}`);
              if (onJobStatusChange) {
                onJobStatusChange();
              }
            }
          }
        }
      } catch (error: any) {
        console.error('Polling error:', error);
        clearInterval(interval);
        setPollingInterval(null);
        
        // Show error message if polling fails
        let errorMessage = 'Failed to check import status.';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        setError(errorMessage);
      }
    }, 2000); // Poll every 2 seconds

    setPollingInterval(interval);
  };

  const handleClose = () => {
    // Clear polling interval if active
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    // Reset state
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setError(null);
    setSuccess(null);
    setImportJob(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'info';
      case 'processing':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'critical';
      case 'cancelled':
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import Call Logs"
      primaryAction={{
        content: 'Upload',
        onAction: handleUpload,
        disabled: !file || uploading,
        loading: uploading,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {success && (
            <Banner tone="success">
              <BlockStack gap="200">
                <p>{success}</p>
                {onNavigateToImportJobs && (
                  <Button
                    variant="tertiary"
                    size="slim"
                    onClick={onNavigateToImportJobs}
                  >
                    View Import Jobs
                  </Button>
                )}
              </BlockStack>
            </Banner>
          )}

          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h3">
                Download Sample File
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                Download our sample CSV file to see the required format and column headers.
              </Text>
              <Button
                icon={ExportIcon}
                onClick={handleDownloadSample}
                disabled={uploading}
              >
                Download Sample CSV
              </Button>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h3">
                Import Call Logs
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                Select a CSV, XLS, or XLSX file to import your call logs. Maximum file size is 50MB.
              </Text>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <Button
                icon={UploadIcon}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                fullWidth
              >
                Choose File
              </Button>

              {file && (
                <InlineStack gap="200" align="start">
                  <Thumbnail
                    source={UploadIcon}
                    alt="File"
                    size="small"
                  />
                  <BlockStack gap="100">
                    <Text variant="bodyMd" as="span" fontWeight="semibold">
                      {file.name}
                    </Text>
                    <Text variant="bodySm" as="span" tone="subdued">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </BlockStack>
                </InlineStack>
              )}

              {uploading && (
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="span">
                    Uploading file...
                  </Text>
                  <ProgressBar progress={uploadProgress} size="small" />
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          {importJob && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h3">
                  Import Progress
                </Text>
                
                <BlockStack gap="200">
                  <InlineStack gap="200" align="space-between">
                    <Text variant="bodyMd" as="span" fontWeight="semibold">
                      Job #{importJob.id}
                    </Text>
                    <Banner tone={getStatusColor(importJob.status) as any}>
                      <p>{getStatusText(importJob.status)}</p>
                    </Banner>
                  </InlineStack>

                  {importJob.status === 'processing' && (
                    <BlockStack gap="200">
                      <Text variant="bodyMd" as="span">
                        Processing {importJob.processed_records} of {importJob.total_records} records...
                      </Text>
                      <ProgressBar progress={importJob.progress} size="small" />
                    </BlockStack>
                  )}

                  {(importJob.status === 'completed' || importJob.status === 'failed') && (
                    <BlockStack gap="200">
                      <List type="bullet">
                        <List.Item>
                          Total records: {importJob.total_records}
                        </List.Item>
                        <List.Item>
                          Successfully imported: {importJob.success_count}
                        </List.Item>
                        <List.Item>
                          Errors: {importJob.error_count}
                        </List.Item>
                        {importJob.created_count > 0 && (
                          <List.Item>
                            Created: {importJob.created_count}
                          </List.Item>
                        )}
                        {importJob.updated_count > 0 && (
                          <List.Item>
                            Updated: {importJob.updated_count}
                          </List.Item>
                        )}
                        {importJob.success_rate > 0 && (
                          <List.Item>
                            Success rate: {importJob.success_rate}%
                          </List.Item>
                        )}
                        {importJob.duration_formatted !== 'N/A' && (
                          <List.Item>
                            Duration: {importJob.duration_formatted}
                          </List.Item>
                        )}
                      </List>
                      
                      {onNavigateToImportJobs && (
                        <Button
                          variant="tertiary"
                          size="slim"
                          onClick={onNavigateToImportJobs}
                        >
                          View All Import Jobs
                        </Button>
                      )}
                    </BlockStack>
                  )}

                  {importJob.status === 'processing' && (
                    <InlineStack gap="200" align="center">
                      <Spinner size="small" />
                      <Text variant="bodySm" as="span" tone="subdued">
                        Processing... This may take a few minutes for large files.
                      </Text>
                    </InlineStack>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>
          )}

          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h3">
                Requirements
              </Text>
              <List type="bullet">
                <List.Item>
                  Maximum file size: 50MB
                </List.Item>
                <List.Item>
                  Maximum records per file: 50,000
                </List.Item>
                <List.Item>
                  Supported formats: CSV, XLS, XLSX
                </List.Item>
                <List.Item>
                  Only one import job can run at a time
                </List.Item>
                <List.Item>
                  Required columns: phone_number, call_type, status, direction
                </List.Item>
                <List.Item>
                  Required for unique identification: Either call_sid OR external_id (or both)
                </List.Item>
                <List.Item>
                  Optional columns: email, country_code, vendor, called_at, recording_url, duration
                </List.Item>
                <List.Item>
                  recording_url: URL to download call recording (MP3, WAV, etc.) - recordings will be automatically transcribed
                </List.Item>
                <List.Item>
                  duration: Will be automatically calculated from recording - do not include in CSV for billing accuracy
                </List.Item>
              </List>
            </BlockStack>
          </Card>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default CallLogImport;
